import { addFocusedBy, removeFocusedBy } from '@design/utilities/helpers/body-focused-by'
import { createGesture, dragAction } from '@use-gesture/vanilla'
import type { Gesture } from '@use-gesture/vanilla'

export interface MobileBottomPanelConfig {
  rootEl?: HTMLElement
  class?: string
  onOpen?: (mobileBottomPanel: MobileBottomPanel) => void
  onBeforeOpen?: (mobileBottomPanel: MobileBottomPanel) => void
  onClose?: (mobileBottomPanel: MobileBottomPanel) => void
  onBeforeClose?: (mobileBottomPanel: MobileBottomPanel) => void
  onContentSet?: (mobileBottomPanel: MobileBottomPanel) => void
  onDestroy?: (mobileBottomPanel: MobileBottomPanel) => void
  isMobile?: () => boolean
}

export default class MobileBottomPanel {
  config!: MobileBottomPanelConfig
  element!: HTMLElement
  isOpened!: boolean
  fadeEl!: HTMLElement
  handleEl!: HTMLElement | null
  wrapperEl!: HTMLElement
  closeEl!: HTMLElement | null
  contentEl!: HTMLElement
  gesture!: Gesture

  constructor(element: string | HTMLElement, config: MobileBottomPanelConfig = {}) {
    let el: HTMLElement | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector(element) as HTMLElement
    }

    if (!el || typeof el === 'string') {
      return
    }

    this.element = el

    if (this.element.hasAttribute('data-init')) {
      return
    }

    if (!config.rootEl) {
      config.rootEl = this.element.parentNode as HTMLElement
    }

    if (!config.isMobile) {
      config.isMobile = () => true
    }

    this.config = config
    this.isOpened = false

    this.setElements()

    if (this.config.class) {
      this.element.classList.add(this.config.class)
    }

    this.initEvents()

    this.element.setAttribute('data-init', 'true')
  }

  setElements(): void {
    const wrapperEl = this.element.querySelector('.o-mobile-bottom-panel__wrapper') as HTMLElement
    const fadeEl = this.element.querySelector('.o-mobile-bottom-panel__fade') as HTMLElement
    const contentEl = this.element.querySelector('.o-mobile-bottom-panel__content') as HTMLElement

    this.wrapperEl = wrapperEl
    this.fadeEl = fadeEl
    this.contentEl = contentEl
    this.handleEl = this.element.querySelector('.o-mobile-bottom-panel__handle')
    this.closeEl = this.element.querySelector<HTMLElement>('.o-mobile-bottom-panel__heading > .o-mobile-bottom-panel__close')
  }

  getElement(): HTMLElement {
    return this.element
  }

  initEvents(): void {
    if (this.closeEl) {
      this.fadeEl.addEventListener('click', () => {
        this.close()
      })

      this.closeEl.addEventListener('click', () => {
        this.close()
      })

      document.body.addEventListener('keyup', (e) => {
        if (e.keyCode === 27) {
          this.close()
        }
      })
    }

    if (this.handleEl) {
      const Gesture = createGesture([dragAction])
      this.gesture = Gesture(
        this.handleEl,
        {
          onDragEnd: (state) => {
            if (state.direction[1] === 1) {
              this.toggle()
            }
          },
        },
        {
          drag: {
            axis: 'y',
          },
        }
      )
    }

    this.wrapperEl.addEventListener('transitionend', (e) => {
      if (!this.isOpened && (e.propertyName === 'transform' || e.propertyName === 'opacity')) {
        this.element.setAttribute('hidden', 'hidden')

        if (this.config.rootEl !== this.element.parentNode) {
          this.config.rootEl?.append(this.element)
        }

        if (this.config.onClose) {
          this.config.onClose(this)
        }
      }
    })
  }

  open(): MobileBottomPanel {
    if (!this.config.isMobile?.() || this.isOpened) {
      return this
    }

    addFocusedBy('mobileBottomPanel')

    if (document.body !== this.element.parentNode) {
      document.body.append(this.element)
    }

    setTimeout(() => {
      this.config.onBeforeOpen?.(this)

      this.element.removeAttribute('hidden')
      // trigger reflow to make sure pending styles are applied
      void this.element.offsetHeight
      this.element.setAttribute('data-open', 'true')
      this.element.focus()
      this.isOpened = true

      this.element.dispatchEvent(new CustomEvent('mobileBottomPanel.opened'))

      if (this.config.onOpen) {
        this.config.onOpen(this)
      }
    }, 50)

    return this
  }

  close(): MobileBottomPanel {
    if (!this.config.isMobile?.() || !this.isOpened) {
      return this
    }

    this.config.onBeforeClose?.(this)

    this.element.setAttribute('data-open', 'false')

    removeFocusedBy('mobileBottomPanel')
    this.isOpened = false

    this.element.dispatchEvent(new CustomEvent('mobileBottomPanel.closed'))

    return this
  }

  toggle(): MobileBottomPanel {
    if (this.isOpened) {
      this.close()
    } else {
      this.open()
    }

    return this
  }
}
